import { priorityKeys } from './constants';
import type { SearchResultSection } from './distribute.types';
import { SearchResultSectionType } from './distribute.types';
import { distributeBetweenSections } from './distributeBetweenSections';

export const distributeSearchResultsIntoSections = <T>(
	sections: SearchResultSection<T>[],
	isSearch: boolean,
	maxTotal: number
): SearchResultSection<T>[] => {
	let remaining = maxTotal;

	if (isSearch) {
		const searchResults = sections.find(
			(s) => s.key === SearchResultSectionType.searchResults
		);
		const recentSearches = sections.find(
			(s) => s.key === SearchResultSectionType.recentSearches
		);

		const result: SearchResultSection<T>[] = [];

		if (searchResults && recentSearches) {
			const [recentItems, searchItems] = distributeBetweenSections(
				[recentSearches, searchResults],
				maxTotal
			);
			result.push({ ...recentSearches, items: recentItems });
			result.push({ ...searchResults, items: searchItems });
		} else {
			if (recentSearches) {
				result.push({
					...recentSearches,
					items: recentSearches.items.slice(0, maxTotal),
				});
			}
			if (searchResults) {
				result.push({
					...searchResults,
					items: searchResults.items.slice(0, maxTotal),
				});
			}
		}

		return result;
	}

	const orderedSections = priorityKeys
		.map((key) => sections.find((s) => s.key === key))
		.filter(Boolean) as SearchResultSection<T>[]; // filter out undefined

	const result: SearchResultSection<T>[] = [];

	for (const sec of orderedSections) {
		if (result.some((r) => r.key === sec.key)) {
			continue;
		}
		if (remaining <= 0) {
			result.push({ ...sec, items: [] });
			continue;
		}

		const isRecentOrPopularSection =
			sec.key === SearchResultSectionType.recentSearches ||
			sec.key === SearchResultSectionType.recentVisited ||
			sec.key === SearchResultSectionType.popular;

		if (isRecentOrPopularSection) {
			const recentSearches = orderedSections.find(
				(s) => s.key === SearchResultSectionType.recentSearches
			);
			const recentVisited = orderedSections.find(
				(s) => s.key === SearchResultSectionType.recentVisited
			);

			const popular = orderedSections.find(
				(s) => s.key === SearchResultSectionType.popular
			);

			if (recentSearches && recentVisited && popular) {
				const [searchSlice, visitedSlice, popularSlice] =
					distributeBetweenSections(
						[recentSearches, recentVisited, popular],
						remaining
					);

				result.push({ ...recentSearches, items: searchSlice });
				result.push({ ...recentVisited, items: visitedSlice });
				result.push({ ...popular, items: popularSlice });

				remaining -=
					searchSlice.length + visitedSlice.length + popularSlice.length;
				continue;
			}
		}

		const slice = sec.items.slice(0, remaining);
		remaining -= slice.length;

		result.push({ ...sec, items: slice });
	}

	return result;
};
